import { useCallback, useEffect, useRef } from 'react';
import {
  parseEmbeddedSignupSessionInfo,
  type tEmbeddedSignupSessionInfo,
} from 'src/lib/facebook/embeddedSignup';

/**
 * Captures the WhatsApp Embedded Signup `sessionInfo` (`waba_id` /
 * `phone_number_id`) posted by Meta during the `FB.login` popup.
 *
 * The window `message` listener is mounted for the lifetime of the consumer so
 * it is already attached when the popup fires its `WA_EMBEDDED_SIGNUP` message.
 * The captured ids are stored in a ref (not state) so reading them inside the
 * `FB.login` code callback never races a re-render. Call `reset()` at the start
 * of every connection attempt so a stale value from a previous popup cannot
 * leak into a later one.
 *
 * Forwarding these ids to the backend is required for Coexistence (CoEx)
 * onboardings, whose token does not carry the WABA in `granular_scopes`.
 */
export function useEmbeddedSignupSession() {
  const sessionInfoRef = useRef<tEmbeddedSignupSessionInfo | null>(null);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const info = parseEmbeddedSignupSessionInfo(event);
      if (info) sessionInfoRef.current = info;
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const reset = useCallback(() => {
    sessionInfoRef.current = null;
  }, []);

  return { sessionInfoRef, reset } as const;
}
